/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import {
  Button,
  ImageList,
  ImageListItem,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Alert,
  Tooltip,
  Drawer,
} from "@mui/material";
import VectorDown from "@/app/csr-src/assets/icons/VectorDown.svg";
import InfoIcon from "@/app/csr-src/assets/icons/TipIcon.svg";
import CartShipping from "@/app/csr-src/assets/icons/CartShipping.svg";
import EmptyBox from "@/app/csr-src/assets/icons/EmptyBox.svg";
import {
  CartData,
  SubtotalInfo,
  FreightBasicInfo,
  ProductItemInfo,
  BodyParam,
  PROMOTION_KEY,
  SUB_TOTAL_DETAIL_KEY,
  SubtotalDetailItemCoreInfo,
} from "@/app/csr-src/apis/cart/cart.interface";
import { uuidv4 } from "@/app/csr-src/utils/uuidUtils";
import ShipModal from "./component/shipModal";
import { getUserUUIDFromCookie } from "@/utils/userUtils";
import { observer } from "mobx-react";
import styles from "./cartOrder.module.scss";
import { useNavigate } from "react-router-dom";
import callAfterLogin from "@/common-components-src/js/utils/callAfterLogin";
import JDIPrice from "@/common-components-src/js/price/index";
import { imageUtil } from "@/common-components-src/js/utils/imageUtil";
import { getGift } from "@/common-components-src/js/utils/giftUtils";
import { useTranslation } from "@/i18n/client";

import { sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25226";
import { freeTrack, selectSkuIdAndNum } from "./utils/cartTrackUtils";

interface CartItemProps {
  cartData: CartData | null;
  loadDataCheck?: (bodyParam: BodyParam) => void;
  tabValue?: string;
  refreshList?: () => void;
}

const CartOrder: React.FC<CartItemProps> = observer(
  ({ cartData, loadDataCheck, tabValue, refreshList }) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [productItems, setProductItems] = useState<{ info: ProductItemInfo }[] | null>(null);
    const [subtotal, setSubtotal] = useState<{ info: SubtotalInfo } | null>(null);
    const [freight, setFreight] = useState<{ info: FreightBasicInfo } | null>(null);
    const [subtotalDetail, setSubtotalDetail] = useState<
      { info: SubtotalDetailItemCoreInfo }[] | null
    >(null);
    const [againDelete, setAgainDelete] = useState(false); // 删除二次确认弹窗
    const [curDeleteItem, setCurDeleteItem] = useState<any | null>(null); // 要删除的当前item
    const [deleteButtonVisible, setDeleteButtonVisible] = useState<{ [key: number]: boolean }>({}); // 用于控制删除按钮的显示状态
    const [openShipDrawer, setOpenShipDrawer] = useState(false); // 打开拼购
    const navigate = useNavigate();

    useEffect(() => {
      if (!cartData || !cartData.data) {
        return;
      }
      // 商品楼层
      // const productItem = Object.values(cartData.data).filter((item) => {
      //   if (
      //     "type" in item &&
      //     item.type &&
      //     item.type.namespace === "core.trade-DM#cartView.product-M#productItem" &&
      //     "C-M#productItem&basic" in item.info &&
      //     item.info["C-M#productItem&basic"].activeCheck === 1
      //   ) {
      //     return true;
      //   }
      //   return false;
      // }) as Array<{ info: ProductItemInfo }>;
      // console.log("productItems 1111", productItem);
      // setProductItems(productItem);
      const hierarchyRoot = cartData.hierarchy.root || [];
      const productItem: { info: ProductItemInfo }[] = [];

      function processHierarchyKey(key: string) {
        if (!cartData) {
          return;
        }
        const item = cartData.data[key];
        if (
          item &&
          "type" in item &&
          item.type &&
          item.type.namespace === "core.trade-DM#cartView.product-M#productItem" &&
          "C-M#productItem&basic" in item.info &&
          item?.info["C-M#productItem&basic"]?.activeCheck === 1
        ) {
          productItem.push(item as { info: ProductItemInfo });
        }
        const subKeys = cartData.hierarchy[key] || [];
        subKeys.forEach(processHierarchyKey);
      }
      hierarchyRoot.forEach(processHierarchyKey);
      setProductItems(productItem);

      // 商品总量信息楼层
      const subTotals = Object.values(cartData.data).filter((item) => {
        if (
          "type" in item &&
          item.type &&
          item.type.namespace === "core.trade-DM#cartView.subtotal-M#defaultSubtotalItem"
        ) {
          return true;
        }
        return false;
      }) as Array<{ info: SubtotalInfo }>;
      if (subTotals && subTotals.length > 0) {
        setSubtotal(subTotals["0"]);
      }

      // 商品包邮信息楼层
      const freeShip = Object.values(cartData.data).filter((item) => {
        if (
          "type" in item &&
          item.type &&
          item.type.namespace === "jd_main-DM#cartView.freight-M#basic"
        ) {
          return true;
        }
        return false;
      }) as Array<{ info: FreightBasicInfo }>;
      if (freeShip && freeShip.length > 0) {
        setFreight(freeShip["0"]);
      }

      // 商品价格明细信息楼层
      const subtotalDetail = Object.values(cartData.data).filter((item) => {
        if ("type" in item && item.type && item.type.namespace === SUB_TOTAL_DETAIL_KEY) {
          return true;
        }
        return false;
      }) as Array<{ info: SubtotalDetailItemCoreInfo }>;
      console.log("subtotalDetail---", subtotalDetail);
      if (subtotalDetail && subtotalDetail.length > 0) {
        // 排序 subtotalDetail
        subtotalDetail.sort((a, b) => {
          const seqA = a.info?.["C-M#subtotalDetailItem&core"]?.seq || 0; // 默认值为0
          const seqB = b.info?.["C-M#subtotalDetailItem&core"]?.seq || 0; // 默认值为0
          return seqA - seqB; // 从小到大排序
        });
        setSubtotalDetail(subtotalDetail);
      }
    }, [cartData]);

    const handleExpand = () => {
      setIsExpanded(!isExpanded);
    };

    const handleClickOpen = () => {
      setOpenShipDrawer(true);
    };

    const handleClose = () => {
      setOpenShipDrawer(false);
    };

    const handleAgainClose = () => {
      setAgainDelete(false);
      setCurDeleteItem(null);
    };

    const handleItemCheck = () => {
      if (!curDeleteItem) {
        return;
      }
      const uuid = uuidv4();
      let products = {};
      let promotions = {};
      if (curDeleteItem?.partList && curDeleteItem?.partList.length > 0) {
        const partItem = curDeleteItem?.partList?.filter(
          (item: any) =>
            item?.info?.[PROMOTION_KEY]?.activeCheck === 1 &&
            item?.info?.[PROMOTION_KEY]?.promoType === "10",
        );
        // 总价促销如果有赠品则需要添加这些入参
        if (partItem && partItem.length > 0) {
          promotions = {
            ...promotions,
            id: uuid,
            parentId: uuid,
            promotionId: partItem?.[0]?.info?.[PROMOTION_KEY]?.id,
            itemType: "3",
          };
          products = {
            ...products,
            parentId: uuid,
          };
        }
      }
      products = {
        ...products,
        skuId: curDeleteItem?.info?.["C-M#productItem&core"]?.skuId,
        num: curDeleteItem?.info?.["C-M#productItem&basic"]?.num,
        itemType: curDeleteItem?.info?.["HJM-M#cartProduct&extInfo"]?.itemType,
        id: uuid,
      };
      promotions = {
        ...promotions,
        num: curDeleteItem?.info?.["C-M#productItem&basic"]?.num,
        childList: [uuid],
      };
      const requestData: BodyParam = {
        verticalTag: "cn_ybxt_b2c",
        area: "659011275-659011342-0-0",
        cartuuid: getUserUUIDFromCookie(),
        operations: {
          products: [products],
          promotions: [promotions],
        },
        userActionId: "unselect",
        extendParamMap: {
          stock: true,
          tabId: tabValue,
        },
      };
      if (loadDataCheck) {
        loadDataCheck(requestData);
        handleAgainClose();
      }
    };

    const freeItem = () => {
      return (
        <>
          {freight && (
            <div
              className={styles["free-shipping"]}
              exptag={"exp|" + EXPOSURE_RECORD.Shopcart_FreeShippingPromptStrip_Expo}
              data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
              data-exptag-json-param={JSON.stringify(freeTrack(freight))}
              onClick={() => {
                sendClickTrack.Shopcart_FreeShippingPromptStrip_Click({ ...freeTrack(freight) });
                if(freight?.info["HJM-M#basic&basic"]?.isFreeFreight || freight?.info["HJM-M#basic&basic"]?.displayType === 4 ){
                  return;
                }
                location.href = "/s-for-addon?type=2";
              }}
            >
              <div className={styles["info"]}>
                <CartShipping width={20} />
                <span
                  className={styles["spaced"]}
                  dangerouslySetInnerHTML={{
                    __html: freight.info["HJM-M#basic&basic"].coudanMsgRichText,
                  }}
                ></span>
              </div>
              {/* displayType 为4代表月黑风高pin，不需要凑单入口 */}
            {(freight?.info["HJM-M#basic&basic"]?.displayType !== 4 && !freight?.info["HJM-M#basic&basic"]?.isFreeFreight) && (
              parseFloat(freight?.info["HJM-M#basic&basic"]?.totalPriceNeed) <
                parseFloat(freight?.info["HJM-M#basic&basic"]?.freePostThreshold) ? (
                <a className={styles["go"]} href="/s-for-addon?type=2" onClick={() => {
                  // 去拼购
                  sendClickTrack.Shopcart_FreeShippingPromptStrip_Click({ ...freeTrack(freight) });
                }}>
                  {t("cart.priceInfo.go")}
                  <VectorDown width={10} className={styles["svg-go"]} />
                </a>
              ) : (
                <a className={styles["go"]} href="/s-for-addon?type=2" onClick={() => {
                  // 其他逻辑
                  sendClickTrack.Shopcart_FreeShippingPromptStrip_Click({ ...freeTrack(freight) });
                }}>
                  <VectorDown width={10} className={styles["svg-go"]} />
                </a>
              )
            )}
            </div>
          )}
        </>
      );
    };

    const subItem = () => {
      return (
        <div style={{ marginTop: "16px" }}>
          {subtotalDetail &&
            subtotalDetail.length > 0 &&
            subtotalDetail.map((markInfo, index: number) => {
              const info = markInfo?.info?.["C-M#subtotalDetailItem&core"];
              console.log("subtotalDetail info", info);
              return (
                <div key={index}>
                  {/* 0130：新增 adjustType："s" 且 type === "7"，展示free */}
                  {(info?.type === "7" ||
                    (info?.type !== "7" && info?.price && Number(info?.price) > 0)) && (
                      <div
                        className={styles["price-container"]}
                        style={{ height: info?.type === "6" ? "48px" : "34px " }}
                      >
                        <span style={{ display: "flex" }}>
                          <span>
                            {info?.name && (
                              <span className={styles["info-left-bold"]}>{info?.name}</span>
                            )}
                            <span className={styles["info-left"]}>{info?.subItemName}</span>
                          </span>
                          {/* 0130：新增 adjustType："s"，展示free */}
                          {info?.type === "7" && (
                            <Tooltip
                              title={t('cart.priceInfo.freeTips')}
                              arrow
                              placement="top"
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    backgroundColor: "#000",
                                  },
                                },
                                arrow: {
                                  sx: {
                                    color: "#000",
                                  },
                                },
                              }}
                            >
                              <span className={styles["icon"]}>
                                <InfoIcon />
                              </span>
                            </Tooltip>
                          )}
                        </span>
                        {/* 0130：新增 adjustType："s"，展示free */}
                        {info?.adjustType === "s" ? (
                          <span
                            style={{ fontSize: "14px" }}
                            dangerouslySetInnerHTML={{
                              __html: info?.price,
                            }}
                          ></span>
                        ) : (
                          <span style={{ display: "flex" }}>
                            {info?.adjustType && (
                              <span className={styles["info-right-bold"]}>{info?.adjustType}</span>
                            )}
                            {subtotal?.info["HJM-D#subtotal&price"]?.totalSavePrice &&
                              info?.type === "6" &&
                              Number(subtotal?.info["HJM-D#subtotal&price"]?.totalSavePrice) > 0 && (
                                <span
                                  style={{ color: "#CC0C1C", display: "flex", alignItems: "center" }}
                                >
                                  {t("cart.priceInfo.saved")}
                                  <JDIPrice
                                    symbol={cartData?.localInfo?.currencySymbol || "£"}
                                    price={subtotal?.info["HJM-D#subtotal&price"]?.totalSavePrice}
                                    uiType={2}
                                    containerStyle={{
                                      fontSize: "14px",
                                      marginLeft: "4px",
                                      lineHeight: "18px",
                                      color: "#CC0C1C",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  />
                                </span>
                              )}
                            <JDIPrice
                              symbol={cartData?.localInfo?.currencySymbol || "£"}
                              price={info?.price}
                              uiType={2}
                              containerStyle={
                                info?.type === "6"
                                  ? {
                                    marginLeft: "8px",
                                    fontSize: "18px",
                                    lineHeight: "24px",
                                    color: "#1A1A1A",
                                  }
                                  : {
                                    fontSize: "14px",
                                    lineHeight: "18px",
                                    color:
                                      info?.adjustType || info?.type === "5"
                                        ? "#CC0C1C"
                                        : "#1A1A1A",
                                  }
                              }
                            />
                          </span>
                        )}
                      </div>
                    )}
                  {(info?.type === "4" || info?.type === "7") && (
                    <Divider className={styles["line"]} />
                  )}
                </div>
              );
            })}
        </div>
      );
    };

    const isSubtotalNum = () => {
      if (subtotal?.info["HJM-D#subtotal&number"]?.selectedProductNum) {
        return Number(subtotal?.info["HJM-D#subtotal&number"]?.selectedProductNum) > 0;
      }
      return false;
    };

    const subtotalNum = () => {
      return subtotal && subtotal.info["HJM-D#subtotal&number"].selectedProductNum
        ? subtotal.info["HJM-D#subtotal&number"].selectedProductNum
        : "0";
    };

    const toggleDeleteButton = (index: number) => {
      setDeleteButtonVisible((prev) => ({
        ...prev,
        [index]: !prev[index],
      }));
    };

    return (
      <div
        className={styles["container"]}
        exptag={"exp|" + EXPOSURE_RECORD.Shopcart_ProductCard_AddCartDetailLayer_Expo}
        data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
        data-exptag-json-param={{}}
      >
        <div className={styles["top-select"]}>
          <span className={styles["info-left"]}>{t("cart.priceInfo.summary")}</span>
          {isSubtotalNum() && (
            <span className={styles["info-right"]}>
              {`${t("cart.priceInfo.total")} ${subtotalNum()} ${subtotalNum() !== "1" ? `${t("cart.priceInfo.items")}` : `${t("cart.priceInfo.items_plural")}`}`}
            </span>
          )}
        </div>
        {productItems && (
          <ImageList
            cols={4}
            rowHeight={75}
            style={{ overflowY: "hidden", gap: "10px", marginTop: "16px", overflowX: "hidden" }}
          >
            {(isExpanded ? productItems : productItems.slice(0, 12)).map((item, index) => (
              <ImageListItem
                key={item?.info["C-M#productItem&core"]?.skuId}
                className={styles["image-item"]}
                onMouseEnter={() => toggleDeleteButton(index)}
                onMouseLeave={() => toggleDeleteButton(index)}
              >
                <img
                  style={{
                    borderRadius: "4px",
                    objectFit: "contain",
                    width: "75px",
                    height: "75px",
                  }}
                  src={`${imageUtil.concatImgUrlWithoutForceHttps(item?.info["C-M#productItem&basic"]?.imgUrl ? item.info["C-M#productItem&basic"].imgUrl : "", 120, 120)}`}
                  loading="lazy"
                />
                {deleteButtonVisible[index] && (
                  <div className={styles["delete-btn"]}>
                    <div
                      className={styles["btn-text"]}
                      onClick={() => {
                        setAgainDelete(true);
                        setCurDeleteItem(item);
                      }}
                    >
                      {t("cart.priceInfo.delete")}
                    </div>
                  </div>
                )}
              </ImageListItem>
            ))}
          </ImageList>
        )}
        {productItems && productItems.length > 12 && (
          <div className={styles["expanded"]} onClick={() => handleExpand()}>
            {isExpanded ? `${t("cart.priceInfo.retract")}` : `${t("cart.priceInfo.viewAll")}`}
            <VectorDown
              width={10}
              className={isExpanded ? styles["svg-expanded"] : styles["svg-expand"]}
            />
          </div>
        )}
        {freeItem()}
        {isSubtotalNum() && subItem()}
        {subtotal?.info?.["HJM-D#subtotal&price"]?.isZeroOrder === "1" && (
          <div
            exptag={"exp|" + EXPOSURE_RECORD.Shopcart_ZeroPurchaseOrderStrip_Expo}
            data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
            data-exptag-json-param={JSON.stringify({ displayText: subtotal?.info?.["HJM-D#subtotal&price"]?.zeroOrderMsg })}
          >
            <Alert severity="warning" style={{ marginTop: "16px", marginBottom: "16px" }}>
              {subtotal?.info?.["HJM-D#subtotal&price"]?.zeroOrderMsg}
            </Alert>
          </div>
        )}
        {!isSubtotalNum() && (
          <div className={styles["empty-box"]}>
            <EmptyBox /> {t("cart.priceInfo.emptyText")}
          </div>
        )}
        <Button
          disabled={!isSubtotalNum()}
          className={styles["button-check"]}
          exptag={"exp|" + EXPOSURE_RECORD.Shopcart_CheckoutBtn_Expo}
          data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
          data-exptag-json-param={{}}
          onClick={() => {
            callAfterLogin(
              () => {
                getGift().then(() => { });
                navigate("/checkout?immediatelyBuy=false");
              },
              {
                isRefresh: false,
              },
            );
            sendClickTrack.Shopcart_CheckoutBtn_Click(selectSkuIdAndNum(productItems || []));
          }}
        >
          {t("cart.priceInfo.checkOut")} ({subtotalNum()}
          {""})
        </Button>
        <div className={styles["accept"]}>{t("cart.priceInfo.accept")}</div>
        <img
          width={336}
          height={20}
          src="https://st.joy-sourcing.com/website/shopCart/Frame%202036083825%20%282%29.png"
        />
        {/* 删除二次确认弹窗 */}
        <Dialog
          open={againDelete}
          onClose={handleAgainClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t("cart.deleteText")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleAgainClose}>
              {t("cart.cancel")}
            </Button>
            <Button variant="contained" onClick={handleItemCheck}>
              {t("cart.confirm")}
            </Button>
          </DialogActions>
        </Dialog>
        {/* 去拼购 */}
        <Drawer anchor="right" open={openShipDrawer} onClose={() => setOpenShipDrawer(false)}>
          {freight && (
            <ShipModal
              freight={freight?.info}
              handleClose={handleClose}
              cartData={cartData}
              refreshList={refreshList}
            />
          )}
        </Drawer>
      </div>
    );
  },
);

export default CartOrder;
